import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, Typography, Divider } from '@mui/material';
import { AdminPanelSettings, School } from '@mui/icons-material';
import { AuthLayout } from '../components/layout/AuthLayout';
import { UserTypeSelector } from '../components/auth/UserTypeSelector';
import { ExecutorLoginForm } from '../components/auth/ExecutorLoginForm';
import { BusinessLoginForm } from '../components/auth/BusinessLoginForm';
import { useAuth } from '../contexts/AuthContext';

type AuthStep = 'select' | 'executor' | 'business' | 'manager' | 'education';

interface AuthPageProps {
  initialStep?: AuthStep;
}

export const AuthPage: React.FC<AuthPageProps> = ({ initialStep }) => {
  const [step, setStep] = useState<AuthStep>(initialStep || 'select');
  const [searchParams] = useSearchParams();
  const [isVerifying, setIsVerifying] = useState(false);
  const hasProcessedCallback = useRef(false);
  const navigate = useNavigate();
  const { verifyBusiness } = useAuth();

  const handleManagerCallback = useCallback(async (code: string, phone: string) => {
    if (hasProcessedCallback.current || isVerifying) return; // Предотвращаем повторные вызовы
    
    hasProcessedCallback.current = true;
    setIsVerifying(true);
    
    try {
      await verifyBusiness({ phone, code });
      // Очищаем URL параметры после успешной верификации
      navigate('/dashboard', { replace: true });
    } catch (error: any) {
      console.error('Ошибка авторизации менеджера:', error);
      
      // Очищаем URL параметры чтобы остановить зацикливание
      navigate('/auth', { replace: true });
      
      // Показываем форму авторизации менеджера с ошибкой
      setStep('manager');
      setIsVerifying(false);
      
      // НЕ сбрасываем hasProcessedCallback.current - это предотвратит повторные попытки
      // hasProcessedCallback.current останется true, чтобы избежать зацикливания
    }
  }, [isVerifying, verifyBusiness, navigate]);

  // Обработка возврата от MTS ID для менеджеров
  useEffect(() => {
    const code = searchParams.get('code');
    const phone = searchParams.get('phone');
    
    if (code && phone && !hasProcessedCallback.current && !isVerifying) {
      // Это возврат от MTS ID - автоматически верифицируем менеджера
      handleManagerCallback(code, phone);
    }
  }, [searchParams, handleManagerCallback, isVerifying]);

  const handleUserTypeSelect = (type: 'executor' | 'business') => {
    setStep(type);
  };

  const handleBack = () => {
    setStep('select');
  };

  const handleSuccess = () => {
    // Перенаправляем на соответствующий дэшборд
    navigate('/dashboard');
  };

  const renderContent = () => {
    switch (step) {
      case 'executor':
        return (
          <ExecutorLoginForm 
            onBack={handleBack}
            onSuccess={handleSuccess}
          />
        );
      case 'business':
      case 'manager':
      case 'education':
        return (
          <BusinessLoginForm 
            onBack={handleBack}
            onSuccess={handleSuccess}
          />
        );
      default:
        return (
          <>
            <UserTypeSelector onSelect={handleUserTypeSelect} />
            
            {/* Дополнительные ссылки для входа */}
            <Box sx={{ mt: 4 }}>
              <Divider sx={{ mb: 3 }}>
                <Typography variant="body2" color="textSecondary">
                  Специальные входы
                </Typography>
              </Divider>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Button
                  variant="outlined"
                  startIcon={<AdminPanelSettings />}
                  onClick={() => setStep('manager')}
                  sx={{
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    py: 1.5,
                    px: 2
                  }}
                >
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      Вход для менеджеров
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Панель администрирования системы
                    </Typography>
                  </Box>
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<School />}
                  onClick={() => setStep('education')}
                  sx={{
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    py: 1.5,
                    px: 2
                  }}
                >
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      Вход для образовательных учреждений
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Управление заданиями и учениками
                    </Typography>
                  </Box>
                </Button>
              </Box>
            </Box>
          </>
        );
    }
  };

  const getTitle = () => {
    switch (step) {
      case 'executor':
        return 'Вход для исполнителя';
      case 'business':
        return 'Вход для бизнеса';
      case 'manager':
        return 'Вход для менеджеров';
      case 'education':
        return 'Вход для образовательных учреждений';
      default:
        return 'Добро пожаловать!';
    }
  };

  const getSubtitle = () => {
    switch (step) {
      case 'executor':
        return 'Авторизация через систему "Мой налог"';
      case 'business':
        return 'Авторизация через MTS ID';
      case 'manager':
        return 'Панель администрирования системы';
      case 'education':
        return 'Управление заданиями и учениками';
      default:
        return 'Для начала выберите один из двух направлений';
    }
  };

  return (
    <AuthLayout
      title={getTitle()}
      subtitle={getSubtitle()}
    >
      {renderContent()}
    </AuthLayout>
  );
};
